import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { WebSocketService } from '@/services/websocketService';
import { audioService } from '@/services/audioService';

interface WebSocketContextValue {
  isConnected: boolean;
  error: string | null;
  isPlaying: boolean;
  sessionId: string | null;
  surveyId: string | null;
  sendText: (text: string, language?: string) => void;
  sendAudio: (audioData: Blob) => void;
  sendInteractionResponse: (value: any, language?: string) => void;
  on: (messageType: string, handler: (data: any) => void) => void;
  off: (messageType: string) => void;
  resumeAudio: () => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

// Store WebSocket instance outside React lifecycle
let wsInstance: WebSocketService | null = null;
let wsToken: string | null = null;
let mountCount = 0;

interface WebSocketProviderProps {
  token: string;
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ token, children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [surveyId, setSurveyId] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    mountCount++;
    isMountedRef.current = true;
    console.log(`🔌 WebSocketProvider mounted (count: ${mountCount}, token: ${token})`);

    // Create new instance only if token changed or no instance exists
    if (!wsInstance || wsToken !== token) {
      console.log(`🆕 Creating new WebSocket instance (old token: ${wsToken}, new token: ${token})`);

      // Clean up old instance if it exists
      if (wsInstance) {
        console.log('🧹 Destroying old WebSocket instance');
        wsInstance.destroy();
      }

      // Get user's language preference from localStorage
      const userLanguage = localStorage.getItem('language') || 'en';
      console.log('🌐 User language preference from localStorage:', userLanguage);

      wsInstance = new WebSocketService(token);
      wsToken = token;

      // Set up session init handler
      wsInstance.on('session_init', (data: any) => {
        if (isMountedRef.current) {
          console.log('📝 Received session_init:', data);
          setSessionId(data.session_id);
          setSurveyId(data.survey_id);
        }
      });

      // Set up audio chunk handler
      wsInstance.on('audio_chunk', async (audioBlob: Blob) => {
        if (isMountedRef.current) {
          setIsPlaying(true);
          await audioService.playAudio(audioBlob);
          setIsPlaying(audioService.getPlayingState());
        }
      });

      // Connect
      console.log(`🚀 Connecting WebSocket to token: ${token}`);
      wsInstance.connect()
        .then(() => {
          if (isMountedRef.current) {
            console.log('✅ WebSocket connected successfully');
            setIsConnected(true);
            setError(null);

            // Send language preference immediately after connection
            setTimeout(() => {
              console.log('📤 Sending set_language message with:', userLanguage);
              wsInstance?.send(JSON.stringify({
                type: 'set_language',
                data: { language: userLanguage }
              }));
            }, 100);
          }
        })
        .catch((err) => {
          console.error('❌ WebSocket connection failed:', err);
          if (isMountedRef.current) {
            setError('Failed to connect to server');
          }
        });
    } else {
      console.log(`♻️  Reusing existing WebSocket connection (token: ${token})`);
      // Reusing existing connection
      setIsConnected(wsInstance.isConnected());
    }

    return () => {
      mountCount--;
      isMountedRef.current = false;
      console.log(`🔌 WebSocketProvider unmounted (remaining count: ${mountCount})`);

      // Only destroy if this is the last unmount and token matches
      setTimeout(() => {
        if (mountCount === 0 && wsInstance && wsToken === token) {
          console.log('🗑️  Destroying WebSocket instance (no more mounts)');
          wsInstance.destroy();
          wsInstance = null;
          wsToken = null;
        }
      }, 100);
    };
  }, [token]);

  const sendText = useCallback((text: string, language: string = 'en') => {
    wsInstance?.sendText(text, language);
  }, []);

  const sendAudio = useCallback((audioData: Blob) => {
    wsInstance?.sendAudio(audioData);
  }, []);

  const sendInteractionResponse = useCallback((value: any, language: string = 'en') => {
    wsInstance?.sendInteractionResponse(value, language);
  }, []);

  const on = useCallback((messageType: string, handler: (data: any) => void) => {
    if (wsInstance && !wsInstance.isDestroyed()) {
      wsInstance.on(messageType, handler);
    }
  }, []);

  const off = useCallback((messageType: string) => {
    if (wsInstance && !wsInstance.isDestroyed()) {
      wsInstance.off(messageType);
    }
  }, []);

  const resumeAudio = useCallback(() => {
    audioService.resume();
  }, []);

  const value: WebSocketContextValue = {
    isConnected,
    error,
    isPlaying,
    sessionId,
    surveyId,
    sendText,
    sendAudio,
    sendInteractionResponse,
    on,
    off,
    resumeAudio,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): WebSocketContextValue => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
